// Lightweight contextual-help primitives: a hover/focus tooltip help icon and
// a smart empty-state block. Government-grade, restrained, accessible.
//
// No external dependencies beyond lucide-react (already used across the portal).

import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, Info } from 'lucide-react';
import { trackGuidance } from '@/lib/onboarding';

/* ──────────── HelpTip: accessible icon + tooltip ──────────── */

interface HelpTipProps {
  text: string;
  label?: string;
  /** topic id for non-invasive analytics */
  topic?: string;
}

export const HelpTip: React.FC<HelpTipProps> = ({ text, label = 'More information', topic }) => {
  const [open, setOpen] = useState(false);
  const seenRef = useRef(false);

  const reveal = () => {
    setOpen(true);
    if (!seenRef.current) {
      seenRef.current = true;
      trackGuidance('help_viewed', { topic });
    }
  };

  return (
    <span className="relative inline-flex items-center align-middle font-eds-body">
      <button
        type="button"
        aria-label={label}
        className="text-eds-muted-fg hover:text-eds-emerald-2 focus:text-eds-emerald-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring rounded-eds-pill"
        onMouseEnter={reveal}
        onMouseLeave={() => setOpen(false)}
        onFocus={reveal}
        onBlur={() => setOpen(false)}
        onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          role="tooltip"
          className="absolute z-50 left-1/2 -translate-x-1/2 top-6 w-56 px-3 py-2 rounded-eds-md bg-eds-forest text-white text-xs leading-relaxed shadow-eds-md border border-eds-forest-2"
        >
          <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-eds-forest border-l border-t border-eds-forest-2" aria-hidden="true" />
          {text}
        </span>
      )}
    </span>
  );
};

/* ──────────── SmartEmptyState ──────────── */

interface SmartEmptyStateProps {
  title?: string;
  explanation: string;
  nextStep?: string;
  actionLabel?: string;
  onAction?: () => void;
  icon?: React.ComponentType<{ className?: string }>;
}

export const SmartEmptyState: React.FC<SmartEmptyStateProps> = ({
  title = 'Nothing here yet',
  explanation,
  nextStep,
  actionLabel,
  onAction,
  icon: Icon = Info,
}) => (
  <div className="text-center border border-dashed border-eds-border rounded-eds-lg p-8 bg-eds-surface-2/50 font-eds-body">
    <div className="mx-auto w-11 h-11 rounded-eds-pill bg-eds-surface border border-eds-border flex items-center justify-center mb-3 text-eds-emerald-2">
      <Icon className="w-5 h-5" />
    </div>
    <h4 className="font-eds-display text-sm font-bold text-eds-fg">{title}</h4>
    <p className="text-sm text-eds-muted-fg mt-1 max-w-md mx-auto leading-relaxed">{explanation}</p>
    {nextStep && (
      <p className="text-xs text-eds-muted-fg mt-2 max-w-md mx-auto">
        <span className="font-semibold text-eds-fg">Recommended next step: </span>
        {nextStep}
      </p>
    )}
    {actionLabel && onAction && (
      <button
        type="button"
        onClick={onAction}
        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-eds-primary hover:bg-eds-emerald-2 text-eds-primary-fg text-sm font-semibold rounded-eds-pill transition focus:outline-none focus-visible:ring-2 focus-visible:ring-eds-ring focus-visible:ring-offset-2 focus-visible:ring-offset-eds-bg"
      >
        {actionLabel}
      </button>
    )}
  </div>
);

/* ──────────── tiny utility: close on Escape (shared) ──────────── */

export function useEscapeToClose(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onClose]);
}
